import { useRef, useState } from "react";
import { Camera } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { AuthImage } from "@/components/AuthImage";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const KINDS = ["pickup", "delivery", "damage", "document"];

// Proof photos on a shipment: pick a file or shoot one on the phone.
export const ShipmentPhotos = ({ shipment, onDone }) => {
  const fileRef = useRef(null);
  const [kind, setKind] = useState("delivery");
  const [pct, setPct] = useState(null);
  const photos = shipment.photos || [];

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Photo must be 8MB or smaller");
      return;
    }
    setPct(0);
    const form = new FormData();
    form.append("file", file);
    form.append("caption", "");
    try {
      const { data } = await api.post("/images", form, {
        onUploadProgress: (evt) => setPct(evt.total ? Math.round((evt.loaded / evt.total) * 100) : 0),
      });
      await api.post(`/shipments/${shipment.id}/photos`, { image_id: data.id, kind });
      toast.success(`${kind} photo added to ${shipment.shipment_number}`);
      onDone?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setPct(null);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="mt-4 border-t border-border pt-3" data-testid={`shipment-photos-${shipment.id}`}>
      <div className="overline mb-2 text-slate-500">Proof photos ({photos.length}/10)</div>
      {photos.length > 0 && (
        <div className="mb-3 flex gap-2 overflow-x-auto">
          {photos.map((p) => (
            <figure key={p.image_id} className="shrink-0" data-testid={`shipment-photo-${p.image_id}`}>
              <AuthImage
                imageId={p.image_id}
                alt={p.caption || p.kind}
                testid={`shipment-photo-img-${p.image_id}`}
                className="h-20 w-20 border border-border object-cover"
              />
              <figcaption className="mt-1 text-[0.6rem] uppercase tracking-wider text-slate-500">
                {p.kind}
              </figcaption>
            </figure>
          ))}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <Select value={kind} onValueChange={setKind}>
          <SelectTrigger data-testid={`photo-kind-${shipment.id}`} className="h-9 w-36 rounded-none border-slate-300 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {KINDS.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}
          </SelectContent>
        </Select>
        <button
          type="button"
          data-testid={`add-photo-${shipment.id}`}
          onClick={() => fileRef.current?.click()}
          className="inline-flex items-center gap-1.5 border border-slate-300 px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-600 transition-colors duration-150 hover:bg-slate-100"
        >
          <Camera size={14} /> Add photo
        </button>
        <input
          ref={fileRef}
          data-testid={`photo-input-${shipment.id}`}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={upload}
          className="hidden"
        />
        {pct !== null && <span className="text-xs text-slate-500">Uploading {pct}%</span>}
      </div>
    </div>
  );
};
